import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";

export default function Cart() {
  const { user, setCartCount } = useAuth();
  const nav = useNavigate();
  const [cart, setCart] = useState({ items: [], subtotal: 0 });
  const [loading, setLoading] = useState(true);

  const load = async () => {
    if (!user) { setLoading(false); return; }
    try {
      const { data } = await api.get("/cart");
      setCart(data);
      setCartCount(data.items.reduce((s, i) => s + i.qty, 0));
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [user]);

  const remove = async (id) => {
    await api.delete(`/cart/${id}`);
    toast.success("Removed");
    load();
  };

  if (user === null || loading) return <div className="p-10 text-center text-[#3B5249]">Loading…</div>;
  if (!user) return (
    <div className="max-w-2xl mx-auto px-4 py-16 text-center">
      <h1 className="font-display text-3xl text-[#0F3D2E]">Sign in to view your cart</h1>
      <Link to="/login"><Button className="mt-6 rounded-full bg-[#0F3D2E] hover:bg-[#14A085] text-white">Sign In</Button></Link>
    </div>
  );

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="text-xs uppercase tracking-[0.2em] text-[#14A085]">Your bag</div>
      <h1 className="font-display text-3xl md:text-4xl text-[#0F3D2E]">Shopping Cart</h1>

      {cart.items.length === 0 ? (
        <div className="mt-10 rounded-xl border border-dashed border-[#E2E8E5] bg-white p-12 text-center" data-testid="empty-cart">
          <p className="text-[#3B5249]">Your cart is empty.</p>
          <Link to="/products"><Button className="mt-4 rounded-full bg-[#0F3D2E] hover:bg-[#14A085] text-white">Start shopping</Button></Link>
        </div>
      ) : (
        <div className="mt-8 grid lg:grid-cols-[1fr_360px] gap-8">
          <div className="space-y-4" data-testid="cart-items">
            {cart.items.map((it) => (
              <div key={it.id} className="rounded-xl border border-[#E2E8E5] bg-white p-4 flex gap-4" data-testid={`cart-item-${it.id}`}>
                <img src={it.images?.[0]} alt="" className="w-24 h-24 rounded-lg object-cover" />
                <div className="flex-1">
                  <Link to={`/products/${it.id}`} className="font-medium text-[#0F3D2E] hover:text-[#14A085]">{it.title}</Link>
                  <div className="text-xs text-[#6E877C] mt-1">by {it.vendor_name}</div>
                  <div className="mt-2 flex items-center justify-between">
                    <div className="text-sm">Qty: <span className="font-medium">{it.qty}</span></div>
                    <div className="text-lg font-semibold text-[#0F3D2E]">₹{(it.price * it.qty).toLocaleString()}</div>
                  </div>
                </div>
                <button onClick={() => remove(it.id)} className="text-red-600 hover:text-red-700 self-start" data-testid={`remove-${it.id}`}>
                  <Trash2 size={18} />
                </button>
              </div>
            ))}
          </div>

          <div className="rounded-xl border border-[#E2E8E5] bg-white p-6 h-fit sticky top-24" data-testid="cart-summary">
            <div className="font-display text-xl text-[#0F3D2E]">Order Summary</div>
            <div className="mt-4 space-y-2 text-sm">
              <div className="flex justify-between text-[#3B5249]">
                <span>Subtotal</span><span>₹{cart.subtotal.toLocaleString()}</span>
              </div>
              <div className="flex justify-between text-[#3B5249]">
                <span>Shipping</span><span className="text-[#14A085]">Free</span>
              </div>
              <div className="border-t border-[#E2E8E5] pt-3 flex justify-between font-semibold text-[#0F3D2E]">
                <span>Total</span><span data-testid="cart-total">₹{cart.subtotal.toLocaleString()}</span>
              </div>
            </div>
            <Button onClick={() => nav("/checkout")} className="w-full mt-6 rounded-full bg-[#0F3D2E] hover:bg-[#14A085] text-white" data-testid="checkout-btn">
              Proceed to Checkout
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
